import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { useRef, useState } from 'react';
import { AddOutline, TrashOutline } from '../../../../../../icons/icons';
import { AircraftEdrSettingRecord, AircraftEdrSettings } from '../../../../../../services/actor-messages';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store-hooks';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';
import { ExtractKeysExtend } from '../../../../../../components/helpers/helpers';
import { produce } from 'immer';
import { setSetting } from '../../../../../state/slices/settings-slice';
import { useBlocker } from 'react-router';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const defaults = [
    { name: 'Light', key: 'light' },
    { name: 'Medium', key: 'medium' },
    { name: 'Heavy', key: 'heavy' }
] satisfies Array<{ name: string; key: ExtractKeysExtend<AircraftEdrSettings, AircraftEdrSettingRecord> }>;

export default function SettingsAircraftEdrOverrides() {
    useSettingsPageWidth('700px');
    const dispatch = useAppDispatch();
    const idSource = useRef(0);
    const [edited, setEdited] = useState(false);
    const [duplicateError, setDuplicateError] = useState(false);
    const edrSettings = useAppSelector((x) => x.settings.aircraftEdrSettings);
    const [editCopy, setEditCopy] = useState(() => ({
        ...edrSettings,
        custom: edrSettings.custom.map((x) => ({
            ...x,
            id: idSource.current++
        }))
    }));
    const blocker = useBlocker(edited);

    const addCustom = () => {
        setEdited(true);
        setEditCopy((p) => ({
            ...p,
            custom: [
                ...p.custom,
                {
                    id: idSource.current++,
                    type: '',
                    moderate: null,
                    severe: null,
                    extreme: null
                }
            ]
        }));
    };

    const removeCustom = (id: number) => {
        setEdited(true);
        setEditCopy((p) => ({
            ...p,
            custom: p.custom.filter((x) => x.id !== id)
        }));
    };

    const editDefault = (
        weight: ExtractKeysExtend<AircraftEdrSettings, AircraftEdrSettingRecord>,
        type: keyof AircraftEdrSettingRecord,
        value: number
    ) => {
        setEdited(true);
        setEditCopy((p) => ({
            ...p,
            [weight]: {
                ...p[weight],
                [type]: value
            }
        }));
    };

    const editCustomValue = (id: number, type: keyof AircraftEdrSettingRecord, value: number) => {
        setEdited(true);

        setEditCopy(
            produce(editCopy, (draft) => {
                const item = draft.custom.find((x) => x.id === id);
                if (item) {
                    item[type] = value;
                }
            })
        );
    };

    const editCustomType = (id: number, value: string) => {
        setEdited(true);

        setEditCopy(
            produce(editCopy, (draft) => {
                const item = draft.custom.find((x) => x.id === id);
                if (item) {
                    item.type = value.toUpperCase();
                }
            })
        );
    };

    const save = () => {
        setDuplicateError(false);
        const filtered = editCopy.custom.filter((x) => x.type);
        const newEditCopy = {
            ...editCopy,
            custom: filtered
        };
        setEditCopy(newEditCopy);

        const hasDuplicates = filtered.length !== new Set(filtered.map((x) => x.type)).size;
        if (hasDuplicates) {
            setDuplicateError(true);
            return false;
        }

        dispatch(
            setSetting('aircraftEdrSettings', {
                ...newEditCopy,
                // Remap it to remove ID fields
                custom: newEditCopy.custom.map((x) => ({
                    type: x.type,
                    moderate: x.moderate,
                    severe: x.severe,
                    extreme: x.extreme
                }))
            })
        );

        setEdited(false);
        setDuplicateError(false);
        return true;
    };

    return (
        <div>
            {blocker.state === 'blocked' && (
                <Dialog title="Aircraft EDR overrides" modal={true} onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar layout="stretched">
                        <Button
                            themeColor={'primary'}
                            onClick={() => {
                                if (save()) {
                                    blocker.proceed();
                                } else {
                                    blocker.reset();
                                }
                            }}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Aircraft types overrides</div>
                    <Button disabled={!edited} fillMode={'outline'} onClick={save}>
                        Save
                    </Button>
                </div>
            </SettingsNavTitle>

            <div className="k-card p-3 overflow-auto max-h-100">
                {duplicateError && (
                    <div className="text-error secondary-text pb-2">
                        Duplicate aircraft types detected. Please ensure all custom types are unique.
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) max-content', gap: '0.5rem' }}>
                    <div>Aircraft type</div>
                    <div>Moderate</div>
                    <div>Severe</div>
                    <div>Extreme</div>
                    <div></div>

                    {defaults.map((x) => (
                        <div key={x.name} style={{ display: 'contents' }}>
                            <TextBox value={x.name} readOnly />
                            <NumericTextBox
                                onChange={(e) => editDefault(x.key, 'moderate', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={editCopy[x.key].moderate}
                            />
                            <NumericTextBox
                                onChange={(e) => editDefault(x.key, 'severe', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={editCopy[x.key].severe}
                            />
                            <NumericTextBox
                                onChange={(e) => editDefault(x.key, 'extreme', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={editCopy[x.key].extreme}
                            />
                            <div></div>
                        </div>
                    ))}

                    <div style={{ gridColumn: '1/6' }} className="d-flex justify-content-between align-items-end">
                        <div>Custom:</div>
                        <Button themeColor={'primary'} svgIcon={AddOutline} onClick={addCustom}></Button>
                    </div>

                    {editCopy.custom.length === 0 && (
                        <div style={{ gridColumn: '1/6' }} className="secondary-text text-center">
                            No overrides
                        </div>
                    )}

                    {editCopy.custom.map((x) => (
                        <div key={x.id} style={{ display: 'contents' }}>
                            <TextBox value={x.type} onChange={(e) => editCustomType(x.id, e.value as string)} />
                            <NumericTextBox
                                onChange={(e) => editCustomValue(x.id, 'moderate', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={x.moderate}
                            />
                            <NumericTextBox
                                onChange={(e) => editCustomValue(x.id, 'severe', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={x.severe}
                            />
                            <NumericTextBox
                                onChange={(e) => editCustomValue(x.id, 'extreme', e.value as number)}
                                format={'0.00'}
                                step={0.01}
                                max={1}
                                value={x.extreme}
                            />
                            <Button
                                svgIcon={TrashOutline}
                                themeColor={'error'}
                                fillMode={'outline'}
                                onClick={() => removeCustom(x.id)}></Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
